/**
 * PATH: packages/email-hub/src/feature/reader-header/adapters/ReaderHeaderAdapter.tsx
 * FILE_ID: EMAILHUB-READERHEADER-ADAPTER-FRAME-001
 * PURPOSE: Render the shared frame + overlays from neutral host bindings.
 * ROLE IN ARCHITECTURE: The single assembly both surface adapters delegate to.
 * MAIN EXPORTS: ReaderHeaderAdapter (internal to adapters/).
 * NON-RESPONSIBILITIES: Hosts own controllers; the frame owns band layout.
 * NOTES FOR FUTURE AI: This component is hooks + guards + JSX only. New
 *   behavior goes into useReaderHeaderCommands (uniform) or bindings (host-varying).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'

import { EmailContextChips } from '../../components/email-context/EmailContextChips'
import { PingTeammateButton } from '../../components/PingTeammateButton'
import { PingNotificationCard } from '../../components/PingNotificationCard'
import { usePingHandoff } from '../../components/usePingHandoff'
import { usePersonalMembershipCount } from '../../personal'
import { ProviderWritebackControls } from '../../provider-writeback'
import { WhoCanSeeButton } from '../../components/WhoCanSeeButton'
import { ReaderHeaderFrame } from '../ReaderHeaderFrame'
import { buildReaderHeaderViewModel } from '../model/buildReaderHeader'
import { buildEmailNotices } from '../notices/buildEmailNotices'
import { ReaderDecisionActions } from '../triage/DecisionActions'
import { ExpandedContext } from '../triage/ExpandedContext'
import { ReaderPlacementsControl } from '../placements/PlacementManager'
import { ReaderSnoozeDialog } from '../command/SnoozeDialog'
import { ReaderShortcutHelp } from '../command/ShortcutHelp'
import { useReaderHeaderShortcuts } from '../command/useReaderHeaderShortcuts'
import { useReaderActionRunner } from '../runtime/useReaderActionRunner'
import { useReaderMutationSync } from '../runtime/multiTabSync'
import { useReaderHeaderCommands } from './useReaderHeaderCommands'
import type { ReaderHeaderHostBindings } from './types'

export function ReaderHeaderAdapter(bindings: ReaderHeaderHostBindings) {
  const { email, surface, kanban } = bindings
  const [snoozeOpen, setSnoozeOpen] = useState(false)
  const [visibilityOpen, setVisibilityOpen] = useState(false)
  // Optimistic queue advance: ids resolved locally before the server echo
  // rebuilds the email payload. Reset whenever a different email mounts.
  const [resolvedDecisionIds, setResolvedDecisionIds] = useState<string[]>([])
  useEffect(() => {
    setResolvedDecisionIds([])
  }, [email.id])
  const markResolved = useCallback(
    (decisionId: string) =>
      setResolvedDecisionIds((current) =>
        current.includes(decisionId) ? current : [...current, decisionId],
      ),
    [],
  )
  const unmarkResolved = useCallback(
    (decisionId: string) =>
      setResolvedDecisionIds((current) =>
        current.filter((id) => id !== decisionId),
      ),
    [],
  )

  // ONE runner per mounted header: command bar, More menu, and the decision
  // line share busy phases, idempotency keys, and the reconcile budget.
  const runner = useReaderActionRunner({
    surface,
    reconcile: kanban.onChanged,
  })

  // Multi-tab reconcile (plan stale-overwrite glitch kill): a sibling tab's
  // placement/workflow mutation on THIS email triggers the one reconcile.
  useReaderMutationSync(email.id, kanban.onChanged)

  // Detection ONLY (change #269): buildEmailNotices is a pure synchronous
  // selector and must never fetch, so the one GET that used to live inside
  // PingNotificationCard's own effect now runs here, once, and the result is
  // threaded into the selector via `pingHandoff` — mirroring the pre-existing
  // `detailError` precedent below. See usePingHandoff.ts's file header and
  // PingNotificationCard.tsx's mount-effect note for why detection here and
  // the "viewed" audit stamp (inside PingNotificationCard, on expand) are
  // deliberately NOT the same moment.
  // Cross-organisation decisions are only actionable for members of 2+
  // organisations. This shares the ['personal','organisation'] cache entry the
  // sidebar already warms, so the gate costs no extra request. `?? 0` fails
  // closed while it loads — better a suggestion that appears late than one
  // that appears wrongly to a single-org operator.
  const memberOrgCount = usePersonalMembershipCount().data ?? 0
  const pingHandoff = usePingHandoff(email.id)
  const notices = useMemo(
    () =>
      buildEmailNotices(email, {
        detailError: bindings.detailError,
        pingHandoff,
      }),
    [email, bindings.detailError, pingHandoff],
  )
  const model = useMemo(
    () =>
      buildReaderHeaderViewModel({
        email,
        surface,
        shellOrgId: kanban.orgId,
        memberOrgCount,
        notices,
        resolvedDecisionIds,
      }),
    [email, surface, kanban.orgId, memberOrgCount, notices, resolvedDecisionIds],
  )
  const commands = useReaderHeaderCommands(
    bindings,
    model,
    {
      openSnooze: () => setSnoozeOpen(true),
      openVisibility: () => setVisibilityOpen(true),
    },
    runner,
  )
  const { helpOpen, setHelpOpen } = useReaderHeaderShortcuts({
    onFind: bindings.onFind,
    onDone: commands.done.onClick,
  })

  return (
    <>
      <ReaderHeaderFrame
        email={email}
        surface={surface}
        shellOrgId={kanban.orgId}
        memberOrgCount={memberOrgCount}
        narrow={bindings.narrow}
        positionLabel={commands.positionLabel}
        onBack={bindings.onBack}
        backLabel={bindings.backLabel}
        previous={commands.previous}
        next={commands.next}
        reply={commands.reply}
        replyAll={commands.replyAll}
        forward={commands.forward}
        done={commands.done}
        overflowActions={commands.overflowActions}
        onFind={bindings.onFind}
        onExpand={bindings.onExpand}
        notices={notices}
        resolvedDecisionIds={resolvedDecisionIds}
        identityRelated={
          bindings.relatedContext && (
            <EmailContextChips
              email={email}
              onJumpTo={bindings.relatedContext.onOpenEmail}
            />
          )
        }
        decisionContent={(density) => (
          <ReaderDecisionActions
            decision={model.currentDecision}
            decisions={model.decisions}
            email={email}
            api={kanban.api}
            density={density}
            runner={runner}
            onChanged={kanban.onChanged}
            onPatchEmail={kanban.onPatchEmail}
            markResolved={markResolved}
            unmarkResolved={unmarkResolved}
            onOpenCard={() => kanban.openAllocatedCard()}
            onOpenBoard={kanban.openAllocatedBoard}
            onOpenBill={kanban.openBillDoc}
          />
        )}
        summaryContent={
          <ReaderPlacementsControl
            email={email}
            api={kanban.api}
            orgId={kanban.orgId}
            onPatchEmail={kanban.onPatchEmail}
            onChanged={kanban.onChanged}
            onOpenCard={() => kanban.openAllocatedCard()}
            onOpenBoard={() => kanban.openAllocatedBoard()}
            surface={surface}
            // The compressed line now carries this control while a decision is
            // pending too (change #294), so it must honour D269-12: the
            // decision line owns the ONE confirm/reject pair, the chip shows
            // no second AI tick/cross.
            suppressReviewControls={model.decisions.length > 0}
          />
        }
        expandedContext={(density) => (
          <ExpandedContext
            email={email}
            api={kanban.api}
            orgId={kanban.orgId}
            density={density}
            suppressDecisionAffordances={model.decisions.length > 0}
            onRefetch={kanban.onChanged}
            onPatchEmail={kanban.onPatchEmail}
            onFilterCompany={kanban.onFilterCompany}
            onOpenCard={() => kanban.openAllocatedCard()}
            onOpenBoard={kanban.openAllocatedBoard}
            onPeekJob={kanban.onPeekJob}
            canRequestRule={kanban.canRequestRule}
            onOpenRuleDialog={kanban.onOpenRuleDialog}
            allocationMembers={kanban.allocationMembers}
            surface={surface}
            onPing={() => bindings.onPingOpenChange(true)}
            hideSenderIdentity={kanban.hideSenderIdentity}
            hideJobLink={!bindings.onLinkJob}
          />
        )}
        noticeDetails={{
          // A provider_writeback result maps to EITHER the `provider` kind
          // (failed) or the `info` kind (requested/running/completed/
          // restored/skipped) — see buildEmailNotices.ts — but both expand
          // to the SAME real control band, verbatim, so both keys point at
          // one mount. Only one of the two kinds can ever be present at
          // once for a given email.
          provider: (
            <ProviderWritebackControls
              key={email.id}
              email={email}
              onChanged={kanban.onChanged}
            />
          ),
          info: (
            <ProviderWritebackControls
              key={email.id}
              email={email}
              onChanged={kanban.onChanged}
            />
          ),
          ping: (
            <PingNotificationCard
              emailId={email.id}
              allocationSummary={email.allocation_summary}
              handoff={pingHandoff}
            />
          ),
        }}
      />
      <div className="fixed right-3 top-12 z-[80] [&_[data-testid=email-ping-teammate-button]]:hidden">
        <PingTeammateButton
          emailId={email.id}
          onPinged={kanban.onChanged}
          open={bindings.pingOpen}
          onOpenChange={bindings.onPingOpenChange}
        />
      </div>
      <div className="fixed right-3 top-12 z-[80] [&_[data-testid=email-who-can-see-button]]:hidden">
        <WhoCanSeeButton
          emailId={email.id}
          open={visibilityOpen}
          onOpenChange={setVisibilityOpen}
        />
      </div>
      <ReaderSnoozeDialog
        open={snoozeOpen}
        onClose={() => setSnoozeOpen(false)}
        onSelect={commands.onSnoozeSelect}
      />
      <ReaderShortcutHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  )
}
